import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import ShareRow from '../../components/ShareRow/ShareRow';
import DateFields, { chartDateHref, dateFromSearch, dateParam, emptyDate, parseDate } from '../../components/DateFields/DateFields';
import { noteAstrology } from '../../services/chartService';
import { LIFE_PATHS, MASTER_NUMBERS, lifePathFor } from '../../data/astro/lifePath';
import '../Astro/Astro.css';

const ORDER = [1, 2, 3, 4, 5, 6, 7, 8, 9, 11, 22, 33];

const FAQ = [
  {
    question: 'How do I calculate my life path number?',
    answer: 'Reduce your birth month, day, and year to single digits separately, keeping 11, 22, and 33 as they are. Add the three results and reduce again the same way. The calculator above shows every step for your date.',
  },
  {
    question: 'What are master numbers?',
    answer: '11, 22, and 33 are called master numbers in numerology. They are not reduced further because they are read as stronger, more demanding versions of 2, 4, and 6. A master number life path is often described as a bigger gift that takes longer to grow into.',
  },
  {
    question: 'Why do some calculators give a different number?',
    answer: 'Some add every digit of the date in one line instead of reducing the month, day, and year first. Usually the answer is the same, but the two methods can disagree about master numbers. This calculator uses the reduce-each-part method, which most numerologists prefer because it keeps master numbers visible.',
  },
  {
    question: 'Is the life path number the most important number?',
    answer: 'In numerology it is usually treated as the core number, like a Sun sign in astrology. It describes your natural direction and the lessons your life keeps returning to. Other numbers, such as the expression number from your name, add detail.',
  },
];

const LifePath = () => {
  const location = useLocation();
  const [date, setDate] = useState(() => dateFromSearch(location.search) || emptyDate());
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const resultRef = useRef(null);

  const calculate = (value, scroll = true) => {
    const parsed = parseDate(value);
    if (parsed.error) {
      setError(parsed.error);
      return;
    }
    setError('');
    const path = lifePathFor(parsed.year, parsed.month, parsed.day);
    const born = `${parsed.year}-${String(parsed.month).padStart(2, '0')}-${String(parsed.day).padStart(2, '0')}`;
    setResult({ ...path, date: parsed });
    noteAstrology('life-path', path.number, born);
    if (scroll) setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 30);
  };

  useEffect(() => {
    const fromUrl = dateFromSearch(location.search);
    if (fromUrl) calculate(fromUrl, false);
    // Only the first render reads the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (event) => {
    event.preventDefault();
    calculate(date);
  };

  const info = result ? LIFE_PATHS[result.number] : null;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebApplication',
        name: 'Life Path Number Calculator',
        url: 'https://nowtarot.com/life-path-number-calculator/',
        applicationCategory: 'LifestyleApplication',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        description: 'Free life path number calculator. Enter your birthday to find your numerology life path number, with every step shown and a full meaning.',
      },
      {
        '@type': 'FAQPage',
        mainEntity: FAQ.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
      },
    ],
  };

  return (
    <div className="astro-page">
      <Helmet>
        <title>Life Path Number Calculator: Free Numerology Meaning | NowTarot</title>
        <meta name="description" content="Free life path number calculator. Enter your birthday, see every step of the numerology math, and read what your life path number means for love, work, and purpose." />
        <link rel="canonical" href="https://nowtarot.com/life-path-number-calculator/" />
        <meta property="og:title" content="Life Path Number Calculator: Free Numerology Meaning" />
        <meta property="og:description" content="Find your life path number from your birthday and read what it means." />
        <meta property="og:url" content="https://nowtarot.com/life-path-number-calculator/" />
        <meta property="og:image" content="https://nowtarot.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Header />
      <main>
        <p className="astro-eyebrow">Tool · Free · Numerology</p>
        <h1>Your <em>life path</em> number</h1>
        <p className="astro-lede">
          Your birthday, reduced the numerologist's way. You'll see every step of the math and what your number says about
          love, work, and the lessons your life keeps coming back to.
        </p>

        <section className="astro-card">
          <form className="astro-form" onSubmit={handleSubmit}>
            <DateFields value={date} onChange={(next) => { setDate(next); setError(''); }} />
            <button className="astro-button" type="submit">Find my number</button>
          </form>
          {error && <p className="astro-error">{error}</p>}
        </section>

        {info && (
          <section className="astro-card" ref={resultRef} aria-live="polite">
            <div className="astro-result-big">
              <span className="astro-number">{result.number}</span>
              <div>
                <p className="astro-eyebrow">
                  Your life path number{MASTER_NUMBERS.has(result.number) ? ' · Master number' : ''}
                </p>
                <h2>{result.number}, {info.title}</h2>
              </div>
            </div>
            <div className="astro-steps">{result.lines.join('\n')}</div>
            <ul className="astro-tags">
              {info.keywords.map((word) => <li key={word}>{word}</li>)}
            </ul>
            <p>{info.summary}</p>
            <h3>Your strengths</h3>
            <p>{info.strengths}</p>
            <h3>Your challenges</h3>
            <p>{info.challenges}</p>
            <h3>In love</h3>
            <p>{info.love}</p>
            <h3>At work</h3>
            <p>{info.work}</p>
            <h3>Most compatible life paths</h3>
            <p>{info.compatible.map((number) => `${number} (${LIFE_PATHS[number].title})`).join(', ')}</p>
            <ShareRow
              url="/life-path-number-calculator/"
              text={`My life path number is ${result.number}, ${info.title}. Find yours:`}
            />
          </section>
        )}

        {result && (
          <section className="astro-section">
            <h2>Keep going</h2>
            <ul className="astro-next">
              <li className="astro-next-lead">
                <a href={chartDateHref(result.date)}>
                  <strong>Your birth chart</strong>
                  <span>Add your birth time and place for every planet, the houses, and a reading you can ask about.</span>
                </a>
              </li>
              <li>
                <a href={`/tarot-birth-card-calculator/?date=${dateParam(result.date)}`}>
                  <strong>Your tarot birth card</strong>
                  <span>The same birthday, read through the Major Arcana.</span>
                </a>
              </li>
              <li>
                <a href="/zodiac-compatibility/"><strong>Zodiac compatibility</strong><span>How your sign matches with theirs, in love.</span></a>
              </li>
            </ul>
          </section>
        )}

        <section className="astro-section">
          <h2>What is a life path number?</h2>
          <p>
            In numerology, your life path number comes from your full date of birth. It is read as the main road of your
            life: your natural strengths, the lessons that keep returning, and the kind of work and love that feel right.
            It never changes, because your birthday never changes.
          </p>
          <div className="astro-next">
            {ORDER.map((number) => (
              <article key={number} className="astro-mini">
                <strong>{number} · {LIFE_PATHS[number].title}</strong>
                <span>{LIFE_PATHS[number].keywords.join(', ')}</span>
              </article>
            ))}
          </div>
        </section>

        <section className="astro-section">
          <h2>Life path number FAQ</h2>
          <div className="astro-faq">
            {FAQ.map((item) => (
              <article key={item.question}>
                <h3>{item.question}</h3>
                <p>{item.answer}</p>
              </article>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default LifePath;
